/* =========================================================
   NISAYI PRODUCT REVIEW VIDEOS
========================================================= */

(function () {
  "use strict";

  const SECTION_SELECTOR = "[data-nsy-prv]";
  const MOBILE_QUERY = window.matchMedia("(max-width: 749px)");

  function playSafely(video) {
    if (!video) return;
    const promise = video.play();
    if (promise && typeof promise.catch === "function") {
      promise.catch(function () {});
    }
  }

  function pauseVideo(video) {
    if (video && !video.paused) video.pause();
  }

  function initSection(section) {
    if (!section || section.dataset.nsyPrvReady === "true") return;
    section.dataset.nsyPrvReady = "true";

    const track = section.querySelector("[data-nsy-prv-track]");
    const cards = Array.from(section.querySelectorAll("[data-nsy-prv-card]"));
    const dots = Array.from(section.querySelectorAll("[data-nsy-prv-dot]"));

    if (!track || !cards.length) return;

    let activeIndex = 0;
    let scrollTimer = null;
    let sectionVisible = false;

    function getVideo(card) {
      return card ? card.querySelector("video") : null;
    }

    function setCardState(card, isPlaying) {
      card.classList.toggle("is-playing", isPlaying);
      card.classList.toggle("is-paused", !isPlaying);

      const button = card.querySelector("[data-nsy-prv-play]");
      if (button) {
        button.setAttribute("aria-pressed", isPlaying ? "true" : "false");
        button.setAttribute("aria-label", isPlaying ? "Pause video review" : "Play video review");
      }
    }

    function updateDots() {
      dots.forEach(function (dot, index) {
        const isActive = index === activeIndex;
        dot.classList.toggle("is-active", isActive);
        if (isActive) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
    }

    function muteAllExcept(activeVideo) {
      document.querySelectorAll(SECTION_SELECTOR + " video").forEach(function (video) {
        if (video === activeVideo) return;
        video.muted = true;
        video.defaultMuted = true;

        const muteButton = video.closest("[data-nsy-prv-card]")?.querySelector("[data-nsy-prv-mute]");
        const icon = muteButton?.querySelector("[data-nsy-prv-mute-icon]");
        if (muteButton) {
          muteButton.setAttribute("aria-pressed", "false");
          muteButton.setAttribute("aria-label", "Unmute video review");
        }
        if (icon) icon.textContent = "🔇";
      });
    }

    function activate(index, shouldPlay) {
      activeIndex = Math.max(0, Math.min(index, cards.length - 1));

      cards.forEach(function (card, cardIndex) {
        const video = getVideo(card);
        const isActive = cardIndex === activeIndex;

        card.classList.toggle("is-active", isActive);

        if (!video) return;

        if (!isActive) {
          pauseVideo(video);
          setCardState(card, false);
          return;
        }

        if (shouldPlay && sectionVisible && !document.hidden) {
          video.muted = true;
          video.defaultMuted = true;
          playSafely(video);
        }
      });

      updateDots();
    }

    function findClosestCard() {
      const trackRect = track.getBoundingClientRect();
      const viewportCenter = trackRect.left + trackRect.width / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      cards.forEach(function (card, index) {
        const rect = card.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const distance = Math.abs(center - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    cards.forEach(function (card, index) {
      const video = getVideo(card);
      const playButton = card.querySelector("[data-nsy-prv-play]");
      const muteButton = card.querySelector("[data-nsy-prv-mute]");
      const muteIcon = card.querySelector("[data-nsy-prv-mute-icon]");

      if (!video) return;

      video.controls = false;
      video.removeAttribute("controls");
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.loop = true;
      video.muted = true;
      video.defaultMuted = true;

      setCardState(card, false);

      video.addEventListener("play", function () {
        setCardState(card, true);
      });

      video.addEventListener("pause", function () {
        setCardState(card, false);
      });

      if (playButton) {
        playButton.addEventListener("click", function (event) {
          event.preventDefault();
          event.stopPropagation();

          if (index !== activeIndex) activate(index, false);

          if (video.paused) playSafely(video);
          else pauseVideo(video);
        });
      }

      if (muteButton) {
        muteButton.addEventListener("click", function (event) {
          event.preventDefault();
          event.stopPropagation();

          if (video.muted) {
            muteAllExcept(video);
            video.muted = false;
            muteButton.setAttribute("aria-pressed", "true");
            muteButton.setAttribute("aria-label", "Mute video review");
            if (muteIcon) muteIcon.textContent = "🔊";
            if (video.paused) playSafely(video);
          } else {
            video.muted = true;
            muteButton.setAttribute("aria-pressed", "false");
            muteButton.setAttribute("aria-label", "Unmute video review");
            if (muteIcon) muteIcon.textContent = "🔇";
          }
        });
      }
    });

    dots.forEach(function (dot, index) {
      dot.addEventListener("click", function () {
        const card = cards[index];
        if (!card) return;

        activeIndex = index;
        updateDots();

        track.scrollTo({
          left: card.offsetLeft - track.offsetLeft,
          behavior: "smooth"
        });

        window.setTimeout(function () {
          activate(index, true);
        }, 280);
      });
    });

    track.addEventListener("scroll", function () {
      if (!MOBILE_QUERY.matches) return;
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(function () {
        activate(findClosestCard(), true);
      }, 120);
    }, { passive: true });

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          sectionVisible = entry.isIntersecting;
          if (sectionVisible) activate(activeIndex, true);
          else cards.forEach(function (card) { pauseVideo(getVideo(card)); });
        });
      }, { threshold: 0.35 });

      observer.observe(section);
    } else {
      sectionVisible = true;
      activate(0, true);
    }

    document.addEventListener("visibilitychange", function () {
      if (document.hidden) {
        cards.forEach(function (card) { pauseVideo(getVideo(card)); });
      } else if (sectionVisible) {
        activate(activeIndex, true);
      }
    });

    updateDots();
  }

  function init(root) {
    (root || document).querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });
})();
